// Curated directory data for launch. Shaped like a future `DirectoryListing` DB
// row so swapping this for an API fetch (admin-managed) is a drop-in change.
import type { ReactNode } from 'react';
import {
  Dumbbell, Waves, Footprints, Trophy, Activity, Heart, Flame, Mountain, Bike, Music,
  Calendar, UserCheck,
} from 'lucide-react';
import { createElement } from 'react';

export type CategoryKey =
  | 'gyms' | 'swimming' | 'running' | 'racket-sports' | 'football' | 'yoga-pilates'
  | 'boxing-mma' | 'climbing' | 'cycling' | 'dance' | 'classes' | 'trainers';

export interface Category {
  key: CategoryKey;
  label: string;
  blurb: string;
  icon: (props: { className?: string }) => ReactNode;
}

export interface Listing {
  slug: string;
  name: string;
  category: CategoryKey;
  area: string;        // suburb / locality
  city: string;
  summary: string;
  address?: string;
  phone?: string;
  website?: string;
  featured?: boolean;
}

const icon = (C: typeof Dumbbell) => (props: { className?: string }) => createElement(C, props);

export const CATEGORIES: Category[] = [
  { key: 'gyms', label: 'Gyms', blurb: 'Strength, cardio and full-service gyms.', icon: icon(Dumbbell) },
  { key: 'swimming', label: 'Swimming', blurb: 'Pools, lessons and lane swimming.', icon: icon(Waves) },
  { key: 'running', label: 'Running', blurb: 'Tracks, clubs and group runs.', icon: icon(Footprints) },
  { key: 'racket-sports', label: 'Racket sports', blurb: 'Tennis, padel, squash and more.', icon: icon(Trophy) },
  { key: 'football', label: 'Football', blurb: 'Pitches, five-a-side and academies.', icon: icon(Activity) },
  { key: 'yoga-pilates', label: 'Yoga & Pilates', blurb: 'Studios, mat classes and reformer.', icon: icon(Heart) },
  { key: 'boxing-mma', label: 'Boxing & MMA', blurb: 'Boxing, kickboxing and martial arts.', icon: icon(Flame) },
  { key: 'climbing', label: 'Climbing', blurb: 'Bouldering and climbing walls.', icon: icon(Mountain) },
  { key: 'cycling', label: 'Cycling', blurb: 'Spin studios and cycling clubs.', icon: icon(Bike) },
  { key: 'dance', label: 'Dance', blurb: 'Dance studios and fitness dance.', icon: icon(Music) },
  { key: 'classes', label: 'Classes', blurb: 'Bootcamps, HIIT and group classes.', icon: icon(Calendar) },
  { key: 'trainers', label: 'Personal trainers', blurb: 'Coaches for one-on-one training.', icon: icon(UserCheck) },
];

export const LISTINGS: Listing[] = [
  { slug: 'iron-peak-fitness', name: 'Iron Peak Fitness', category: 'gyms', area: 'Avondale', city: 'Harare', summary: 'A full-service gym with free weights, machines and group classes.', featured: true },
  { slug: 'city-aquatic-centre', name: 'City Aquatic Centre', category: 'swimming', area: 'CBD', city: 'Harare', summary: 'Olympic-length pool with lane swimming and lessons for all levels.', featured: true },
  { slug: 'eastside-courts', name: 'Eastside Courts', category: 'racket-sports', area: 'Greendale', city: 'Harare', summary: 'Tennis and padel courts available to book day and evening.', featured: true },
  { slug: 'borrowdale-run-club', name: 'Borrowdale Run Club', category: 'running', area: 'Borrowdale', city: 'Harare', summary: 'Free community group runs every weekend, all paces welcome.' },
  { slug: 'northside-boxing', name: 'Northside Boxing', category: 'boxing-mma', area: 'Mount Pleasant', city: 'Harare', summary: 'Boxing and kickboxing classes for beginners to competitors.' },
  { slug: 'flow-yoga-studio', name: 'Flow Yoga Studio', category: 'yoga-pilates', area: 'Newlands', city: 'Harare', summary: 'Daily yoga and reformer Pilates in a calm studio space.' },
  { slug: 'summit-climbing-wall', name: 'Summit Climbing Wall', category: 'climbing', area: 'Msasa', city: 'Harare', summary: 'Indoor bouldering and top-rope walls with gear hire.' },
  { slug: 'coach-tanaka', name: 'Coach Tanaka', category: 'trainers', area: 'Avondale', city: 'Harare', summary: 'Personal trainer specialising in strength and fat loss programmes.' },
  { slug: 'bulawayo-fc-grounds', name: 'Bulawayo FC Grounds', category: 'football', area: 'Hillside', city: 'Bulawayo', summary: 'Five-a-side and full pitches for hire, plus a youth academy.' },
  { slug: 'pedal-spin-studio', name: 'Pedal Spin Studio', category: 'cycling', area: 'Belgravia', city: 'Harare', summary: 'High-energy indoor cycling classes with music and metrics.' },
];

export function listingsByCategory(key: string): Listing[] {
  return LISTINGS.filter((l) => l.category === key);
}

export function getCategory(key: string): Category | undefined {
  return CATEGORIES.find((c) => c.key === key);
}

export function getListing(category: string, slug: string): Listing | undefined {
  return LISTINGS.find((l) => l.category === category && l.slug === slug);
}

export const FEATURED: Listing[] = LISTINGS.filter((l) => l.featured);
